/**
 * Runtime locale file loader with caching
 */

import { createSignal } from 'solid-js';

// Cache for loaded locale files (keyed by basePath:language)
const localesCache: Record<string, Record<string, string>> = {};

// Signal to track cache updates (triggers reactivity when new locales are loaded)
const [cacheVersion, setCacheVersion] = createSignal(0);

/**
 * Get the current cache version (for reactive tracking)
 */
export function getCacheVersion(): number {
  return cacheVersion();
}

/**
 * Load a locale file from the public folder at runtime
 * @param language - The language code (e.g., 'en-US', 'fr-FR')
 * @param basePath - The base path where locale files are stored (default: '/locales')
 * @returns Promise that resolves to the locale data
 */
export async function loadLocale(
  language: string,
  basePath = '/locales'
): Promise<Record<string, string>> {
  // Create cache key that includes basePath for proper isolation
  const cacheKey = `${basePath}:${language}`;

  // Check cache first
  if (cacheKey in localesCache) {
    // eslint-disable-next-line no-console
    console.log(`[i18n] Using cached locale: ${language} (${basePath})`);
    return localesCache[cacheKey];
  }

  // eslint-disable-next-line no-console
  console.log(`[i18n] Loading locale: ${language} from ${basePath}`);

  try {
    // Fetch from configured path
    const url = `${basePath}/${language}.json`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(
        `HTTP ${String(response.status)}: ${response.statusText}`
      );
    }

    const data = (await response.json()) as Record<string, string>;

    // Cache it for next time
    localesCache[cacheKey] = data;

    // Increment cache version to trigger reactivity
    setCacheVersion((v) => v + 1);

    // eslint-disable-next-line no-console
    console.log(`[i18n] Successfully loaded locale: ${language}`);

    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `[i18n] Failed to load locale ${language} from ${basePath}:`,
      error
    );
    throw error;
  }
}

/**
 * Preload a locale file without waiting for it
 * @param language - The language code to preload
 * @param basePath - The base path where locale files are stored (default: '/locales')
 */
export function preloadLocale(language: string, basePath = '/locales'): void {
  void loadLocale(language, basePath);
}

/**
 * Clear the locale cache (useful for development/testing)
 * @param language - Optional: clear only a specific language
 * @param basePath - Optional: clear only locales from a specific base path
 */
export function clearLocaleCache(language?: string, basePath?: string): void {
  if (language && basePath) {
    const cacheKey = `${basePath}:${language}`;
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete localesCache[cacheKey];
    // eslint-disable-next-line no-console
    console.log(`[i18n] Cleared cache for ${language} (${basePath})`);
  } else {
    Object.keys(localesCache).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete localesCache[key];
    });
    // eslint-disable-next-line no-console
    console.log('[i18n] Locale cache cleared');
  }
}

/**
 * Get a cached locale without loading (returns undefined if not cached)
 * @param language - The language code
 * @param basePath - The base path where locale files are stored (default: '/locales')
 * @returns The cached locale data or undefined
 */
export function getCachedLocale(
  language: string,
  basePath = '/locales'
): Record<string, string> | undefined {
  const cacheKey = `${basePath}:${language}`;
  return localesCache[cacheKey];
}
